import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  StackedCellText,
  WrappingCellText,
} from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { HighlightText } from '@/components/common/HighlightText';
import { RowViewLink } from '@/components/common/RowActions';
import { formatDisplayDate, formatDisplayDateTime, formatTime } from '@/utils/date';
import type { FeedbackListItem } from '../types/feedback.types';
import { FeedbackCompletionBadge } from './FeedbackCompletionBadge';
import { FeedbackReadState } from './FeedbackReadState';

export function getFeedbackColumns(searchText: string | undefined): ColumnDef<FeedbackListItem>[] {
  return [
    createSerialColumn<FeedbackListItem>(),
    {
      id: 'user',
      header: 'User',
      cell: ({ row }) => {
        const user = row.original.user;
        if (!user) return <EmptyValue />;
        const isUnread = row.original.readAt === null;
        return (
          <StackedCellText
            primary={<HighlightText text={user.fullName} highlightedText={searchText} />}
            primaryClassName={isUnread ? 'font-bold' : 'font-medium'}
            secondary={<HighlightText text={user.username} highlightedText={searchText} />}
            tertiary={<HighlightText text={user.mobile} highlightedText={searchText} />}
          />
        );
      },
    },
    {
      id: 'thana',
      header: 'Thana',
      cell: ({ row }) => {
        const thana = row.original.user?.thana;
        if (!thana) return <EmptyValue />;
        return <WrappingCellText>{thana}</WrappingCellText>;
      },
    },
    {
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => (
        <WrappingCellText width="wide">
          <ExpandableComment comment={row.original.message} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      id: 'type',
      header: 'Feedback Type',
      cell: ({ row }) => {
        const type = row.original.type;
        if (!type) return <EmptyValue />;
        return <WrappingCellText>{type.name}</WrappingCellText>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <StackedCellText
            primary={<FeedbackCompletionBadge status={status} />}
            secondary={<FeedbackReadState readAt={row.original.readAt} />}
          />
        );
      },
    },
    {
      accessorKey: 'feedbackDate',
      header: 'Entry Date',
      cell: ({ row }) => {
        const { feedbackDate, enteredAt } = row.original;
        return (
          <StackedCellText
            primary={formatDisplayDate(feedbackDate)}
            secondary={enteredAt === null ? null : formatTime(enteredAt)}
          />
        );
      },
    },
    {
      id: 'completed',
      header: 'Completed',
      cell: ({ row }) => {
        const { completedAt, completedByName } = row.original;
        if (!completedAt) return <EmptyValue />;
        return (
          <StackedCellText
            primary={formatDisplayDateTime(completedAt)}
            secondary={completedByName ?? undefined}
          />
        );
      },
    },
    createActionsColumn<FeedbackListItem>((feedback) => (
      <RowViewLink
        to="/feedback/list/$feedbackId"
        params={{ feedbackId: feedback.id }}
        aria-label={
          feedback.user ? `View feedback from ${feedback.user.fullName}` : 'View feedback'
        }
      />
    )),
  ];
}
